import { useState } from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRIMARY_PHONE = "+91 79753 99846";
const SECONDARY_PHONE = "+91 79758 99846";
const PRIMARY_PHONE_TEL = "+917975399846";
const SECONDARY_PHONE_TEL = "+917975899846";
const PRIMARY_PHONE_WA = "917975399846";
const EMAIL = "hello@dreamyinteriors.in";
const STUDIO_ADDRESS = "Hallehalli Road, Hallehalli, Kithiganur, Bengaluru, Karnataka 560036";
const MAPS_LINK = "https://maps.app.goo.gl/uEEgdcfApZ5uhyXm7";
const MAPS_EMBED_SRC = "https://www.google.com/maps?q=Dreamy+Interiors,+Hallehalli+Road,+Kithiganur,+Bangalore+560036&output=embed";

const emptyForm = { name: "", phone: "", email: "", message: "" };

export default function Contact() {
  const waMsg = encodeURIComponent("Hi Dreamy Interiors, I'd like to know more about your services.");
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || submitted) return;   // guard against double-clicks
    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    if (name.length < 2)    { toast.error("Please enter your name."); return; }
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter a valid 10-digit Indian mobile number."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name, phone, email,
        message: form.message.trim() || null,
        source: "Website Contact",
      });
      setSubmitted(true);
      setForm(emptyForm);
      toast.success("Thanks! We'll reach out shortly.");
    } catch {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally { setSubmitting(false); }
  };
  return (
    <div data-testid="contact-page" className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
      <p className="overline">Contact</p>
      <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight mt-4 max-w-3xl leading-[1.05]">
        Visit us. Or call. Or DM. <em className="italic">Whichever feels right.</em>
      </h1>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-8">
          <a
            href={MAPS_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-address-link"
            className="flex gap-5 border-b border-[#E8E6DF] pb-6 group"
          >
            <MapPin size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
            <div>
              <p className="overline">Office</p>
              <p className="mt-2 group-hover:text-[#A07F2D] transition-colors">{STUDIO_ADDRESS}</p>
              <p className="mt-1 text-xs text-[#5C5752]">Open in Google Maps ↗</p>
            </div>
          </a>

          <div className="flex gap-5 border-b border-[#E8E6DF] pb-6">
            <Phone size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
            <div>
              <p className="overline">Call</p>
              <p className="mt-2">
                <a data-testid="contact-phone-primary" href={`tel:${PRIMARY_PHONE_TEL}`} className="hover:text-[#A07F2D] transition-colors">{PRIMARY_PHONE}</a>
              </p>
              <p className="mt-1">
                <a data-testid="contact-phone-secondary" href={`tel:${SECONDARY_PHONE_TEL}`} className="hover:text-[#A07F2D] transition-colors">{SECONDARY_PHONE}</a>
              </p>
              <p className="mt-2 text-xs text-[#5C5752]">Mon–Sat, 9am–5pm</p>
            </div>
          </div>

          <div className="flex gap-5 border-b border-[#E8E6DF] pb-6">
            <Mail size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
            <div>
              <p className="overline">Email</p>
              <p className="mt-2">
                <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="hover:text-[#A07F2D] transition-colors">{EMAIL}</a>
              </p>
            </div>
          </div>

          <div className="flex gap-5 border-b border-[#E8E6DF] pb-6">
            <Clock size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
            <div>
              <p className="overline">Hours</p>
              <p className="mt-2">Office visits by appointment only</p>
            </div>
          </div>

          <a data-testid="contact-whatsapp" href={`https://wa.me/${PRIMARY_PHONE_WA}?text=${waMsg}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-4 uppercase tracking-widest text-xs">
            <MessageCircle size={16} /> WhatsApp us now
          </a>
        </div>

        <div className="lg:col-span-7">
          <a href={MAPS_LINK} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="block aspect-[16/10] rounded-img-lg border border-[#E8E2D5] overflow-hidden">
            <iframe
              data-testid="contact-map"
              title="Dreamy Interiors location"
              src={MAPS_EMBED_SRC}
              className="w-full h-full pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </a>
          <a href={MAPS_LINK} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C5752] hover:text-[#1C1A17]">
            <MapPin size={14} /> Open in Google Maps
          </a>

          {/* Enquiry form */}
          <div className="mt-12 pt-10 border-t border-[#E8E6DF]">
            <p className="overline text-[#A07F2D]">Send us a note</p>
            <h2 className="font-heading text-3xl mt-3">Prefer to write?</h2>
            <p className="text-sm text-[#5C5752] mt-2 mb-8 max-w-md">Share a quick line and we'll get back within one working day. Or WhatsApp us for a faster reply.</p>

            {submitted ? (
              <div data-testid="contact-form-success" className="rounded-2xl border border-[#E8D9B3] bg-[#F5F0E6] p-6">
                <p className="overline text-[#A07F2D]">Received</p>
                <p className="font-heading text-2xl mt-2">Thanks — we'll be in touch shortly.</p>
                <p className="text-sm text-[#5C5752] mt-3">A senior designer will reach out within one working day. Meanwhile, feel free to WhatsApp us for anything urgent.</p>
                <button data-testid="contact-form-reset" onClick={() => setSubmitted(false)} className="mt-6 text-xs uppercase tracking-[0.25em] text-[#5C5752] hover:text-[#1C1A17]">Send another message ↗</button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5752]">Name <span className="text-[#C9A24A]">*</span></label>
                  <input
                    data-testid="contact-form-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-2 py-2 px-0 text-sm bg-transparent border-0 border-b border-[#E8E6DF] focus:border-[#1C1A17] focus:outline-none focus:ring-0"
                    placeholder="Full name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5752]">Phone <span className="text-[#C9A24A]">*</span></label>
                    <input
                      data-testid="contact-form-phone"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="10-digit mobile"
                      className="w-full mt-2 py-2 px-0 text-sm bg-transparent border-0 border-b border-[#E8E6DF] focus:border-[#1C1A17] focus:outline-none focus:ring-0"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5752]">Email <span className="text-[#C9A24A]">*</span></label>
                    <input
                      data-testid="contact-form-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@email.com"
                      className="w-full mt-2 py-2 px-0 text-sm bg-transparent border-0 border-b border-[#E8E6DF] focus:border-[#1C1A17] focus:outline-none focus:ring-0"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5752]">Requirement</label>
                  <textarea
                    data-testid="contact-form-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    placeholder="Apartment / BHK / anything you'd like us to know"
                    className="w-full mt-2 py-2 px-0 text-sm bg-transparent border-0 border-b border-[#E8E6DF] focus:border-[#1C1A17] focus:outline-none focus:ring-0 resize-none"
                  />
                </div>

                <button
                  data-testid="contact-form-submit"
                  type="submit"
                  disabled={submitting}
                  className="mt-2 inline-flex items-center gap-3 bg-[#1C1A17] text-white px-7 py-4 uppercase tracking-widest text-xs hover:bg-[#2D2925] disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send message"}
                </button>
                <p className="text-[10px] text-[#8A847D] mt-3">By submitting, you agree to be contacted about your enquiry. No spam.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
